/**
 * Le moteur de gabarits des pages — un Liquid restreint.
 *
 * POURQUOI UN LANGAGE : un prix recopié dans une page se duplique en jusqu'à
 * 168 exemplaires (21 pages × 8 langues) et devient faux silencieusement le
 * jour où le tarif change. Une liste de modules recopiée devient fausse dès
 * qu'un module est ajouté. Le gabarit DÉSIGNE la donnée ; la base reste seule
 * à la détenir.
 *
 * POURQUOI RESTREINT : le gabarit est stocké dans un champ éditable depuis
 * l'administration. Plus le langage est puissant, plus ce champ devient une
 * surface d'exécution. Sont donc absents, volontairement :
 *
 *   assign, capture   — pas de variables libres, donc pas d'accumulation
 *   include, render   — pas de composition, donc pas de récursion
 *   l'arithmétique    — rien à calculer dans une page de présentation
 *
 * Ce qui reste couvre ce dont les pages ont besoin : parcourir une source,
 * conditionner un affichage, formater une valeur.
 *
 * LES SOURCES SONT EN LISTE BLANCHE. Le gabarit ne peut atteindre que ce que
 * le contexte lui présente — jamais une requête arbitraire.
 *
 * RÈGLE D'ÉCHEC, héritée de TarifBloc : une donnée absente rend une chaîne
 * VIDE, jamais le nom de l'expression, jamais un zéro. Un prix faux est pire
 * qu'un prix absent, et « {{ tarif.BOOKS.prix }} » affiché en clair sur une
 * page publique est une panne visible.
 */

/** Au-delà, un gabarit est probablement une erreur de saisie. */
const PROFONDEUR_MAX = 5;

/** Une boucle ne peut pas geler la page : les sources réelles sont bornées. */
const ITERATIONS_MAX = 500;

/* ------------------------------------------------------------------ */
/* Filtres                                                             */
/* ------------------------------------------------------------------ */

/**
 * Les filtres disponibles, en français comme le reste du vocabulaire des
 * blocs (`titre`, `sousTitre`, `cartes`).
 *
 * `monnaie` et `date` prennent la langue : c'est tout l'intérêt de ne pas
 * recopier un montant, il se formate selon le lecteur.
 */
export const FILTRES = {
    monnaie: (valeur, langue) => {
        // Number(null) et Number('') valent ZÉRO, et zéro est fini : sans ce
        // garde, un tarif introuvable s'afficherait « 0,00 € ». Un prix faux
        // est pire qu'un prix absent — sur un site belge, il engage.
        if (valeur === null || valeur === undefined || valeur === '') return '';
        const n = Number(valeur);
        if (!Number.isFinite(n)) return '';
        try {
            return new Intl.NumberFormat(langue || 'fr', {
                style: 'currency',
                currency: 'EUR'
            }).format(n);
        } catch (e) {
            // Locale inconnue : le montant brut vaut mieux qu'un vide.
            return `${n.toFixed(2)} €`;
        }
    },

    nombre: (valeur, langue) => {
        // Même piège que monnaie : Number(null) vaut 0.
        if (valeur === null || valeur === undefined || valeur === '') return '';
        const n = Number(valeur);
        if (!Number.isFinite(n)) return '';
        try {
            return new Intl.NumberFormat(langue || 'fr').format(n);
        } catch (e) {
            return String(n);
        }
    },

    date: (valeur, langue) => {
        if (!valeur) return '';
        const d = new Date(valeur);
        if (Number.isNaN(d.getTime())) return '';
        try {
            return new Intl.DateTimeFormat(langue || 'fr', {
                year: 'numeric', month: 'long', day: 'numeric'
            }).format(d);
        } catch (e) {
            return d.toISOString().slice(0, 10);
        }
    },

    majuscule: (v) => String(v ?? '').toUpperCase(),
    minuscule: (v) => String(v ?? '').toLowerCase(),

    /** Première lettre en capitale, le reste inchangé. */
    capitale: (v) => {
        const s = String(v ?? '');
        if (!s) return '';
        return s.charAt(0).toUpperCase() + s.slice(1);
    },

    /** Coupe au mot, pour ne pas trancher au milieu d'un terme. */
    tronquer: (v, langue, longueur) => {
        const s = String(v ?? '');
        const max = Number(longueur) || 120;
        if (s.length <= max) return s;
        const coupe = s.slice(0, max);
        const espace = coupe.lastIndexOf(' ');
        if (espace > max * 0.6) return coupe.slice(0, espace) + '…';
        return coupe + '…';
    },

    /** La valeur, ou un repli si elle est vide. */
    defaut: (v, langue, repli) => {
        const s = String(v ?? '').trim();
        if (s) return s;
        return String(repli ?? '');
    }
};

/* ------------------------------------------------------------------ */
/* Lecture d'un chemin dans le contexte                                */
/* ------------------------------------------------------------------ */

/**
 * Découpe un chemin en segments, en résolvant les crochets.
 *
 * `tarif[m.code].prix` devient ['tarif', 'BOOKS', 'prix'] : c'est le seul
 * moyen, dans une boucle, d'atteindre la donnée qui correspond à l'élément
 * courant — « le tarif DE CE module ». Sans cela, une grille de modules ne
 * pourrait pas afficher le prix de chacun.
 *
 * Le contenu des crochets est soit un littéral entre guillemets, soit un
 * chemin résolu dans le contexte. Il ne peut jamais désigner autre chose
 * qu'une clé : pas d'expression, pas d'appel.
 *
 * Rend `null` si le contenu d'un crochet ne se résout pas — l'appelant rend
 * alors `undefined`, donc une chaîne vide.
 */
function decouperChemin(chemin, contexte) {
    const segments = [];
    let tampon = '';
    let i = 0;

    while (i < chemin.length) {
        const c = chemin[i];

        if (c === '.') {
            if (tampon.trim()) segments.push(tampon.trim());
            tampon = '';
            i += 1;
            continue;
        }

        if (c === '[') {
            if (tampon.trim()) segments.push(tampon.trim());
            tampon = '';

            const fin = chemin.indexOf(']', i);
            if (fin === -1) return null;

            const interieur = chemin.slice(i + 1, fin).trim();
            const litteral = interieur.match(/^["'](.*)["']$/);

            if (litteral) {
                segments.push(litteral[1]);
            } else {
                // Un chemin : on le résout dans le contexte pour obtenir la clé.
                const valeur = lireChemin(contexte, interieur);
                if (valeur === null || valeur === undefined) return null;
                segments.push(String(valeur));
            }

            i = fin + 1;
            continue;
        }

        tampon += c;
        i += 1;
    }

    if (tampon.trim()) segments.push(tampon.trim());
    return segments;
}

/**
 * Lit `module.nom`, `tarif.BOOKS.prix` ou `tarif[m.code].prix`.
 *
 * Les clés sont cherchées dans les trois casses : ColdFusion rend du
 * minuscule (queryToArray), le code écrit du camelCase, et certaines
 * réponses remontent en majuscules. Chercher les trois évite de couvrir
 * chaque appelant de `?? x.toLowerCase()`.
 *
 * Rend `undefined` — et non une chaîne — pour que l'appelant distingue
 * « absent » de « vide ».
 */
export function lireChemin(contexte, chemin) {
    if (!contexte || !chemin) return undefined;

    const segments = decouperChemin(String(chemin), contexte);
    if (segments === null) return undefined;

    let courant = contexte;

    for (const seg of segments) {
        if (courant === null || courant === undefined) return undefined;
        if (typeof courant !== 'object') return undefined;

        // Un gabarit ne doit jamais remonter la chaîne de prototypes.
        if (seg === '__proto__' || seg === 'constructor' || seg === 'prototype') {
            return undefined;
        }

        if (seg in courant) {
            courant = courant[seg];
            continue;
        }
        const bas = seg.toLowerCase();
        const haut = seg.toUpperCase();
        if (bas in courant) {
            courant = courant[bas];
            continue;
        }
        if (haut in courant) {
            courant = courant[haut];
            continue;
        }
        return undefined;
    }

    return courant;
}

/* ------------------------------------------------------------------ */
/* Sorties : {{ ... }}                                                 */
/* ------------------------------------------------------------------ */

/**
 * Découpe « chemin | filtre: arg | autre » en ses parties.
 */
function analyserSortie(expression) {
    const parties = String(expression).split('|').map((s) => s.trim());
    const chemin = parties.shift() || '';
    const filtres = parties.map((p) => {
        const i = p.indexOf(':');
        if (i === -1) return { nom: p.trim(), arg: undefined };
        return {
            nom: p.slice(0, i).trim(),
            // Les guillemets encadrant un argument littéral sont retirés.
            arg: p.slice(i + 1).trim().replace(/^["']|["']$/g, '')
        };
    });
    return { chemin, filtres };
}

/**
 * Résout une sortie et applique ses filtres.
 */
function rendreSortie(expression, contexte, langue) {
    const { chemin, filtres } = analyserSortie(expression);

    // Un littéral entre guillemets se rend tel quel : utile comme argument
    // de repli dans `{{ "—" }}`.
    const litteral = chemin.match(/^["'](.*)["']$/);
    let valeur;
    if (litteral) {
        valeur = litteral[1];
    } else {
        valeur = lireChemin(contexte, chemin);
    }

    for (const f of filtres) {
        const fn = FILTRES[f.nom];
        // Filtre inconnu : ignoré plutôt que fatal. Un gabarit valide à
        // l'enregistrement n'en contient pas ; en produire une page blanche
        // serait disproportionné.
        if (typeof fn === 'function') {
            valeur = fn(valeur, langue, f.arg);
        }
    }

    if (valeur === null || valeur === undefined) return '';
    if (typeof valeur === 'boolean') {
        if (valeur) return 'true';
        return '';
    }
    if (typeof valeur === 'object') return '';
    return String(valeur);
}

/* ------------------------------------------------------------------ */
/* Conditions                                                          */
/* ------------------------------------------------------------------ */

/**
 * Ce qui est faux : null, undefined, false, la chaîne vide, le tableau vide.
 *
 * Zéro est VRAI, contrairement à JavaScript : « {% if module.nbUtilisateurs %} »
 * doit s'afficher même à zéro, sinon un compteur à zéro disparaîtrait au
 * lieu d'afficher 0.
 */
function estVrai(valeur) {
    if (valeur === null || valeur === undefined || valeur === false) return false;
    if (valeur === '') return false;
    if (Array.isArray(valeur) && valeur.length === 0) return false;
    return true;
}

/** Compare deux membres, en rendant comparables « 4 » et 4. */
function comparer(gauche, operateur, droite) {
    const nG = Number(gauche);
    const nD = Number(droite);
    const numerique = Number.isFinite(nG) && Number.isFinite(nD);

    switch (operateur) {
        case '==': {
            if (numerique) return nG === nD;
            return String(gauche ?? '') === String(droite ?? '');
        }
        case '!=': {
            if (numerique) return nG !== nD;
            return String(gauche ?? '') !== String(droite ?? '');
        }
        case '>':  if (numerique) return nG > nD;  return String(gauche) > String(droite);
        case '<':  if (numerique) return nG < nD;  return String(gauche) < String(droite);
        case '>=': if (numerique) return nG >= nD; return String(gauche) >= String(droite);
        case '<=': if (numerique) return nG <= nD; return String(gauche) <= String(droite);
        case 'contains': {
            if (Array.isArray(gauche)) {
                return gauche.some((x) => String(x) === String(droite));
            }
            return String(gauche ?? '').includes(String(droite ?? ''));
        }
        default:
            return false;
    }
}

/** Lit un membre : littéral entre guillemets, nombre, ou chemin. */
function lireMembre(texte, contexte) {
    const t = String(texte).trim();
    const litteral = t.match(/^["'](.*)["']$/);
    if (litteral) return litteral[1];
    if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
    if (t === 'true') return true;
    if (t === 'false') return false;
    if (t === 'null' || t === 'nil') return null;
    return lireChemin(contexte, t);
}

/**
 * Évalue une condition : « a == b », « a », « a and b », « a or b ».
 *
 * `and` lie plus fort que `or`, comme en Liquid. Pas de parenthèses :
 * une condition de page n'en a jamais eu besoin, et les ajouter
 * demanderait un analyseur d'expressions complet.
 */
export function evaluerCondition(condition, contexte) {
    const texte = String(condition || '').trim();
    if (!texte) return false;

    // « or » d'abord : il sépare les groupes les plus larges.
    const parOu = texte.split(/\s+or\s+/i);
    if (parOu.length > 1) {
        return parOu.some((p) => evaluerCondition(p, contexte));
    }

    const parEt = texte.split(/\s+and\s+/i);
    if (parEt.length > 1) {
        return parEt.every((p) => evaluerCondition(p, contexte));
    }

    const comparaison = texte.match(/^(.+?)\s+(==|!=|>=|<=|>|<|contains)\s+(.+)$/i);
    if (comparaison) {
        const gauche = lireMembre(comparaison[1], contexte);
        const droite = lireMembre(comparaison[3], contexte);
        return comparer(gauche, comparaison[2].toLowerCase(), droite);
    }

    return estVrai(lireMembre(texte, contexte));
}

/* ------------------------------------------------------------------ */
/* Découpage en jetons                                                 */
/* ------------------------------------------------------------------ */

const MOTIF_JETON = /(\{\{.*?\}\}|\{%.*?%\})/s;

/**
 * Découpe un gabarit en texte, sorties et balises.
 */
export function decouper(gabarit) {
    const source = String(gabarit ?? '');
    if (!source) return [];

    const morceaux = source.split(new RegExp(MOTIF_JETON.source, 'gs'));
    const jetons = [];

    for (const m of morceaux) {
        if (!m) continue;
        if (m.startsWith('{{') && m.endsWith('}}')) {
            jetons.push({ genre: 'sortie', valeur: m.slice(2, -2).trim() });
        } else if (m.startsWith('{%') && m.endsWith('%}')) {
            jetons.push({ genre: 'balise', valeur: m.slice(2, -2).trim() });
        } else {
            jetons.push({ genre: 'texte', valeur: m });
        }
    }

    return jetons;
}

/* ------------------------------------------------------------------ */
/* Rendu                                                               */
/* ------------------------------------------------------------------ */

/**
 * Rend un gabarit dans un contexte.
 *
 * @param {string} gabarit  le texte à rendre
 * @param {object} contexte les sources disponibles (liste blanche)
 * @param {string} langue   pour les filtres monnaie, nombre, date
 * @returns {string}
 */
export function rendre(gabarit, contexte = {}, langue = 'fr') {
    const jetons = decouper(gabarit);
    if (jetons.length === 0) return '';
    const [sortie] = rendreJetons(jetons, 0, contexte, langue, 0);
    return sortie;
}

/**
 * Rend les jetons à partir de `debut`, jusqu'à la fin ou jusqu'à une balise
 * fermante du niveau courant.
 *
 * Rend [texte, index d'arrêt, balise d'arrêt].
 */
function rendreJetons(jetons, debut, contexte, langue, profondeur) {
    let sortie = '';
    let i = debut;

    if (profondeur > PROFONDEUR_MAX) return [sortie, jetons.length, null];

    while (i < jetons.length) {
        const jeton = jetons[i];

        if (jeton.genre === 'texte') {
            sortie += jeton.valeur;
            i += 1;
            continue;
        }

        if (jeton.genre === 'sortie') {
            sortie += rendreSortie(jeton.valeur, contexte, langue);
            i += 1;
            continue;
        }

        // Balise.
        const balise = jeton.valeur;
        const mot = balise.split(/\s+/)[0].toLowerCase();

        if (mot === 'endfor' || mot === 'endif' || mot === 'endunless'
            || mot === 'else' || mot === 'elsif') {
            return [sortie, i, balise];
        }

        if (mot === 'for') {
            const [texte, suivant] = rendreBoucle(jetons, i, contexte, langue, profondeur);
            sortie += texte;
            i = suivant;
            continue;
        }

        if (mot === 'if' || mot === 'unless') {
            const [texte, suivant] = rendreCondition(jetons, i, contexte, langue, profondeur);
            sortie += texte;
            i = suivant;
            continue;
        }

        // Balise inconnue : ignorée silencieusement. La validation à
        // l'enregistrement en a déjà refusé la saisie ; afficher « {% truc %} »
        // au visiteur n'arrangerait rien.
        i += 1;
    }

    return [sortie, i, null];
}

/**
 * Saute le corps d'un bloc sans le rendre, en respectant l'imbrication.
 */
function sauter(jetons, debut, ouvrantes, fermante) {
    let i = debut;
    let niveau = 0;

    while (i < jetons.length) {
        const j = jetons[i];
        if (j.genre === 'balise') {
            const mot = j.valeur.split(/\s+/)[0].toLowerCase();
            if (ouvrantes.includes(mot)) {
                niveau += 1;
            } else if (mot === fermante) {
                if (niveau === 0) return i;
                niveau -= 1;
            }
        }
        i += 1;
    }
    return i;
}

/**
 * {% for x in source %} … {% else %} … {% endfor %}
 *
 * `else` rend le corps de repli quand la source est vide : une grille de
 * modules sans module doit dire quelque chose plutôt que laisser un trou.
 */
function rendreBoucle(jetons, debut, contexte, langue, profondeur) {
    const balise = jetons[debut].valeur;
    const m = balise.match(/^for\s+([A-Za-z_][\w]*)\s+in\s+([\w.]+)\s*$/i);

    if (!m) {
        const fin = sauter(jetons, debut + 1, ['for'], 'endfor');
        return ['', fin + 1];
    }

    const nomVariable = m[1];
    const source = lireChemin(contexte, m[2]);
    const liste = Array.isArray(source) ? source.slice(0, ITERATIONS_MAX) : [];

    let sortie = '';
    let apresCorps = debut + 1;
    let indexElse = -1;

    // Repérer le {% else %} de CE niveau.
    {
        let i = debut + 1;
        let niveau = 0;
        while (i < jetons.length) {
            const j = jetons[i];
            if (j.genre === 'balise') {
                const mot = j.valeur.split(/\s+/)[0].toLowerCase();
                if (mot === 'for') niveau += 1;
                else if (mot === 'endfor') {
                    if (niveau === 0) break;
                    niveau -= 1;
                } else if (mot === 'else' && niveau === 0) {
                    indexElse = i;
                }
            }
            i += 1;
        }
        apresCorps = i;
    }

    const finCorps = indexElse === -1 ? apresCorps : indexElse;

    if (liste.length === 0) {
        if (indexElse !== -1) {
            const [texte] = rendreJetons(
                jetons.slice(indexElse + 1, apresCorps), 0, contexte, langue, profondeur + 1
            );
            sortie += texte;
        }
        return [sortie, apresCorps + 1];
    }

    const corps = jetons.slice(debut + 1, finCorps);

    liste.forEach((element, index) => {
        const contexteBoucle = {
            ...contexte,
            [nomVariable]: element,
            forloop: {
                index: index + 1,
                index0: index,
                first: index === 0,
                last: index === liste.length - 1,
                length: liste.length
            }
        };
        const [texte] = rendreJetons(corps, 0, contexteBoucle, langue, profondeur + 1);
        sortie += texte;
    });

    return [sortie, apresCorps + 1];
}

/**
 * {% if %} … {% elsif %} … {% else %} … {% endif %}
 * {% unless %} … {% endunless %}
 */
function rendreCondition(jetons, debut, contexte, langue, profondeur) {
    const balise = jetons[debut].valeur;
    const mot = balise.split(/\s+/)[0].toLowerCase();
    const fermante = mot === 'unless' ? 'endunless' : 'endif';
    const condition = balise.slice(mot.length).trim();

    // Découper les branches de CE niveau.
    const branches = [];
    let courante = { condition, debut: debut + 1, fin: -1 };
    let i = debut + 1;
    let niveau = 0;

    while (i < jetons.length) {
        const j = jetons[i];
        if (j.genre === 'balise') {
            const m2 = j.valeur.split(/\s+/)[0].toLowerCase();
            if (m2 === 'if' || m2 === 'unless') {
                niveau += 1;
            } else if (m2 === fermante || m2 === 'endif' || m2 === 'endunless') {
                if (niveau === 0) {
                    courante.fin = i;
                    branches.push(courante);
                    break;
                }
                niveau -= 1;
            } else if (niveau === 0 && (m2 === 'elsif' || m2 === 'else')) {
                courante.fin = i;
                branches.push(courante);
                if (m2 === 'else') {
                    courante = { condition: null, debut: i + 1, fin: -1 };
                } else {
                    courante = { condition: j.valeur.slice(5).trim(), debut: i + 1, fin: -1 };
                }
            }
        }
        i += 1;
    }

    if (branches.length === 0 || branches[branches.length - 1].fin === -1) {
        return ['', jetons.length];
    }

    const apres = branches[branches.length - 1].fin + 1;

    for (const b of branches) {
        let retenue;
        if (b.condition === null) {
            retenue = true;
        } else if (mot === 'unless' && b === branches[0]) {
            retenue = !evaluerCondition(b.condition, contexte);
        } else {
            retenue = evaluerCondition(b.condition, contexte);
        }

        if (retenue) {
            const corps = jetons.slice(b.debut, b.fin);
            const [texte] = rendreJetons(corps, 0, contexte, langue, profondeur + 1);
            return [texte, apres];
        }
    }

    return ['', apres];
}

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

/** Ce qu'un gabarit a le droit d'ouvrir. */
const BALISES_CONNUES = ['for', 'endfor', 'if', 'elsif', 'else', 'endif', 'unless', 'endunless'];

/**
 * Vérifie qu'un gabarit est bien formé, AVANT de l'enregistrer.
 *
 * Une erreur doit se voir à la saisie, dans l'administration, et non être
 * découverte par un visiteur devant une page cassée.
 *
 * Rend { valide, erreurs: [] }.
 */
export function validerGabarit(gabarit) {
    const erreurs = [];
    const jetons = decouper(gabarit);
    const pile = [];

    for (const j of jetons) {
        if (j.genre !== 'balise') continue;

        const mot = j.valeur.split(/\s+/)[0].toLowerCase();

        if (!BALISES_CONNUES.includes(mot)) {
            erreurs.push(`Balise inconnue : {% ${mot} %}`);
            continue;
        }

        if (mot === 'for' || mot === 'if' || mot === 'unless') {
            pile.push(mot);
            if (pile.length > PROFONDEUR_MAX) {
                erreurs.push(`Imbrication trop profonde (maximum ${PROFONDEUR_MAX}).`);
            }
        } else if (mot === 'endfor' || mot === 'endif' || mot === 'endunless') {
            const attendu = { endfor: 'for', endif: 'if', endunless: 'unless' }[mot];
            const ouvert = pile.pop();
            if (ouvert !== attendu) {
                erreurs.push(`{% ${mot} %} ne ferme aucun {% ${attendu} %}.`);
            }
        }

        if (mot === 'for' && !/^for\s+[A-Za-z_][\w]*\s+in\s+[\w.]+\s*$/i.test(j.valeur)) {
            erreurs.push(`Boucle mal formée : {% ${j.valeur} %}`);
        }
    }

    if (pile.length > 0) {
        erreurs.push(`Balise non fermée : {% ${pile[pile.length - 1]} %}`);
    }

    return { valide: erreurs.length === 0, erreurs };
}

/**
 * Vrai si le texte contient au moins une expression.
 *
 * Permet de n'appliquer le moteur qu'aux champs qui en ont besoin : la
 * très grande majorité des champs sont du texte ordinaire.
 */
export function contientGabarit(texte) {
    const s = String(texte ?? '');
    return s.includes('{{') || s.includes('{%');
}
